import React, { useEffect } from "react";
import WhatIsIt from "./WhatIsIt";
import WhySection from "./WhySection";
import WhatChanged from "./WhatChanged";
import WhatCanBeDone from "./WhatCanBeDone";
import IncidentsSection from "./IncidentsSection";
import IncidentDetailView from "./IncidentDetailView";
import { N550_SEGMENTS } from "../../mock/n550Segments.mock";

const Skeleton = () => (
  <div
    className="space-y-3 p-4"
    aria-busy="true"
    aria-label="Loading segment data"
  >
    {[80, 120, 96, 64].map((h, i) => (
      <div
        key={i}
        className="animate-pulse rounded-lg bg-gray-100 dark:bg-navy-700"
        style={{ height: h }}
      />
    ))}
  </div>
);

const SegmentDetailPanel = ({
  segmentId,
  detail,
  loading,
  isOpen,
  onClose,
  onSegmentFocus,
  incidents = [],
  selectedIncidentId = null,
  onIncidentClick,
  onIncidentBack,
}) => {
  const label =
    N550_SEGMENTS.find((s) => s.id === segmentId)?.label ?? segmentId;

  const selectedIncident = selectedIncidentId
    ? incidents.find((inc) => inc.id === selectedIncidentId) ?? null
    : null;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        if (selectedIncidentId) onIncidentBack?.();
        else onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose, selectedIncidentId, onIncidentBack]);

  return (
    <aside
      className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm dark:border-white/40 dark:bg-navy-800"
      aria-label={`Segment detail: ${label}`}
    >
      {selectedIncident ? (
        <IncidentDetailView
          incident={selectedIncident}
          segmentLabel={label}
          onBack={onIncidentBack}
        />
      ) : (
        <>
          <div className="shrink-0 border-b border-gray-200 px-4 pb-3 pt-3 dark:border-white/10">
            <button
              onClick={onClose}
              className="mb-1 flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-brand-500 dark:hover:text-brand-400"
              aria-label="Back to corridor summary"
            >
              <span aria-hidden="true">‹</span>
              <span>Corridor Summary</span>
            </button>
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
              {label}
            </p>
            <p className="font-mono text-xs text-gray-400">{segmentId}</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && <Skeleton />}

            {!loading && !detail && (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                No data available for this segment.
              </p>
            )}

            {!loading && detail && (
              <>
                <WhatIsIt detail={detail} />
                <WhySection signals={detail.signals} />
                <WhatChanged
                  campaigns={detail.campaigns}
                  annotations={detail.annotations}
                />
                <WhatCanBeDone
                  detail={detail}
                  onSegmentFocus={onSegmentFocus}
                />
              </>
            )}

            <IncidentsSection
              incidents={incidents.slice(0, 3)}
              onIncidentClick={onIncidentClick}
            />
          </div>
        </>
      )}
    </aside>
  );
};

export default SegmentDetailPanel;
